import React from 'react'
import {keyframes} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '@vio/atlas-tokens'

type Size = 'sm' | 'lg'
export type LoaderVariant = 'brand' | 'inverse'

interface Props {
  /** Loader size */
  size?: Size
  /** Color variant */
  variant?: LoaderVariant
  /** Should remove vertical and horizontal spacing */
  isCompact?: boolean
  /** Explicitly set color. Overrides 'variant' color if passed */
  color?: string
}

const sizesMap = {
  sm: {
    wrapperSize: 22,
    size: 5,
    gap: cssTheme.layout.spacing.s100
  },
  lg: {
    wrapperSize: 44,
    size: 10,
    gap: '3px'
  }
}

const getBounce = ({
  variant,
  color
}: {
  variant: LoaderVariant
  color?: string
}) => keyframes`
  0%, 100% {
    transform: scale(0);
  }
  40%, 60% {
    transform: scale(1.0);
    background-color: ${color ? color : cssTheme.components.loader[variant].content};
  }
`

const Wrapper = styled.div<{
  size: Size
  variant: LoaderVariant
  isCompact: boolean
  color?: string
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({size, isCompact}) =>
    isCompact ? 'auto' : sizesMap[size]?.wrapperSize}px;
  height: ${({size, isCompact}) =>
    isCompact ? 'auto' : sizesMap[size]?.wrapperSize}px;
  gap: ${({size}) => sizesMap[size]?.gap};

  & > div {
    width: ${({size}) => sizesMap[size].size}px;
    height: ${({size}) => sizesMap[size].size}px;
    background-color: transparent;
    border-radius: 50%;
    animation: ${({variant, color}) => getBounce({variant, color})} 1.6s
      ease-in-out infinite both;

    &:nth-of-type(1) {
      animation-delay: -0.32s;
    }

    &:nth-of-type(2) {
      animation-delay: -0.16s;
    }
  }
`
export const Loader = ({
  size = 'sm',
  variant = 'brand',
  isCompact = false,
  color
}: Props) => {
  return (
    <Wrapper size={size} variant={variant} isCompact={isCompact} color={color}>
      <div />
      <div />
      <div />
    </Wrapper>
  )
}
